import { EffectSourceParam, Timeline, TimelineEffect, TimelineState } from '@xzdarcy/react-timeline-editor';
import { Button, Divider, Space, Switch } from 'antd';
import { useEffect, useRef, useState } from 'react';
import './index.less';
import { StoryTimelineAction, StoryTimelineRow } from './effects';
import TimelinePlayer from './player';
import { AiStoryModel, StoryFramesModel } from 'interface/models';
import audioControl from './audioControl';

// Track the logical src of the shared <video> without augmenting DOM typings
const logicalSrcMap = new WeakMap<HTMLVideoElement, string>();

export const EffectStoryFrame = (props: { action: StoryTimelineAction; row: StoryTimelineRow }) => {
  // Можно вернуть только картинку-обложку или null
  const frame = props.action.data.StoryFrame;
  if (!frame) return null;

  let cover = (
    <img
      src={frame.images[0]}
      alt={`Frame ${0}`}
      style={{
        height: '32px',
        maxWidth: '100%',
      }}
    />
  );
  return (
    <div className={'EffectFramePlay'}>
      {cover}
      {/* <LazyVideoPlayer frame={frame} videoSrc={videoSrc} index={0} displayType="small" videoRef={props.videoRef} /> */}
    </div>
  );
};
export const EffectAudioFile = (props: { action: StoryTimelineAction; row: StoryTimelineRow }) => {
  return (
    <div className={'EffectAudioFile'}>
      <div className={`EffectAudioFile`}>Audaio {props.action.data?.AudioFile?.name}</div>
    </div>
  );
};

const TimelineEditor = (props: { frames: StoryFramesModel[]; story: AiStoryModel }) => {
  const [data, setData] = useState<StoryTimelineRow[]>([]);
  const timelineState = useRef<TimelineState>();
  const autoScrollWhenPlay = useRef<boolean>(true);
  const [scale, setScale] = useState(1); // Добавлено состояние для масштаба
  const scaleWidth = 160; // To-do добавить элемент управления настройки
  const startLeft = 20; // To-do добавить элемент управления настройки
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentVideo, setCurrentVideo] = useState<{
    src: string;
    start: number;
    stop: number;
    speed: number;
    frameId: number;
  } | null>(null);
  const stopListenerRef = useRef<((e: Event) => void) | null>(null);
  const activeFrameIdRef = useRef<number | null>(null);

  const safeLoadAndPlay = (opt: { src: string; start: number; stop: number; speed: number; frameId: number }) => {
    const video = videoRef.current;
    if (!video) return;

    // помечаем активный кадр сразу, чтобы leave старого кадра не мешал
    activeFrameIdRef.current = opt.frameId;

    const applyAndPlay = () => {
      if (!video) return;
      try {
        video.currentTime = opt.start;
      } catch (e) {
        setTimeout(() => {
          try {
            video.currentTime = opt.start;
          } catch {}
          video.play().catch(() => {});
        }, 50);
      }
      video.playbackRate = opt.speed;
      if (stopListenerRef.current) {
        video.removeEventListener('timeupdate', stopListenerRef.current);
        stopListenerRef.current = null;
      }
      const stopHandler = () => {
        // останавливаем только если все ещё активен тот же кадр
        if (activeFrameIdRef.current === opt.frameId && video.currentTime >= opt.stop) {
          video.pause();
          video.removeEventListener('timeupdate', stopHandler);
          stopListenerRef.current = null;
        }
      };
      stopListenerRef.current = stopHandler;
      video.addEventListener('timeupdate', stopHandler);
      video.play().catch(() => {});
      setCurrentVideo(opt);
    };

    const prevSrc = logicalSrcMap.get(video);
    if (prevSrc !== opt.src) {
      // перед сменой источника убираем таймер/слушатель от прошлого кадра
      if (stopListenerRef.current) {
        video.removeEventListener('timeupdate', stopListenerRef.current);
        stopListenerRef.current = null;
      }
      video.pause();
      logicalSrcMap.set(video, opt.src);
      video.src = opt.src;
      const onLoaded = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        applyAndPlay();
      };
      video.addEventListener('loadedmetadata', onLoaded);
      video.load();
    } else {
      applyAndPlay();
    }
  };

  useEffect(() => {
    if (!props.frames || props.frames.length === 0) {
      return;
    }

    const timeData: StoryTimelineRow[] = [
      {
        id: 'video-main',
        actions: props.frames.map((frame, index) => {
          return {
            id: 'action' + frame.id,
            start: frame.timeStart / 1000,
            end: frame.timeStart / 1000 + (frame.videoCatStop || 5) - (frame.videoCatStart || 0),
            effectId: 'StoryFrame',
            data: {
              StoryFrame: frame,
            },
          };
        }),
      },
      {
        id: 'audio1',
        actions: [
          {
            id: 'audio1',
            start: 0,
            end: 20,
            effectId: 'AudioFile',
            data: {
              AudioFile: {
                src: `http://localhost:4101/storage/story-${props.story.id}/audio/main-audio-imported.wav`,
                name: 'Audio file',
              },
            },
          },
        ],
      },
    ];
    setData(timeData);
  }, [props.frames]);

  const ProdEffects: Record<string, TimelineEffect> = {
    AudioFile: {
      id: 'AudioFile',
      name: 'Audio File',
      source: {
        start: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          if (param.isPlaying) {
            const src = action.data.AudioFile?.src;
            audioControl.start({ id: src, src, startTime: param.action.start, engine: param.engine, time: param.time });
          }
        },
        enter: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          if (param.isPlaying) {
            const src = action.data.AudioFile?.src;
            audioControl.start({ id: src, src, startTime: action.start, engine: param.engine, time: param.time });
          }
        },
        leave: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          const src = action.data.AudioFile?.src;
          audioControl.stop({ id: src, engine: param.engine });
        },
        stop: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          const src = action.data.AudioFile?.src;
          audioControl.stop({ id: src, engine: param.engine });
        },
      },
    },
    StoryFrame: {
      id: 'StoryFrame',
      name: 'Ai StoryFrame',
      source: {
        start: (param: EffectSourceParam) => {
          console.log('start ' + param.action.id, param);
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          if (!param.isPlaying) return;
          const src = action.data.StoryFrame?.videos?.[0];
          if (!src) return;
          const videoCatStart = action.data.StoryFrame.videoCatStart || 0;
          const videoCatStop = action.data.StoryFrame.videoCatStop || 5;
          const videoSpeed = action.data.StoryFrame.videoSpeed || 1;
          safeLoadAndPlay({ src, start: videoCatStart, stop: videoCatStop, speed: videoSpeed, frameId: action.data.StoryFrame.id });
        },
        enter: (param: EffectSourceParam) => {
          console.log('enter ' + param.action.id, param);
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          if (!param.isPlaying) return;
          const src = action.data.StoryFrame?.videos?.[0];
          if (!src) return;
          const videoCatStart = action.data.StoryFrame.videoCatStart || 0;
          const videoCatStop = action.data.StoryFrame.videoCatStop || 5;
          const videoSpeed = action.data.StoryFrame.videoSpeed || 1;
          // enter всегда перезапускает с начала диапазона
          safeLoadAndPlay({ src, start: videoCatStart, stop: videoCatStop, speed: videoSpeed, frameId: action.data.StoryFrame.id });
        },
        leave: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          const frameId = action.data.StoryFrame?.id;
          const video = videoRef.current;
          // не ставим на паузу, если сейчас играет другой кадр
          if (video && frameId && activeFrameIdRef.current === frameId) {
            video.pause();
          }
        },
        stop: (param: EffectSourceParam) => {
          // @ts-ignore
          const action: StoryTimelineAction = param.action;
          const frameId = action.data.StoryFrame?.id;
          const video = videoRef.current;
          if (video && frameId && activeFrameIdRef.current === frameId) {
            video.pause();
          }
        },
      },
    },
  };

  return (
    <>
      <Space direction="horizontal">
        <Switch
          checkedChildren="Auto scroll when playing"
          unCheckedChildren="Disable auto scroll when playing"
          defaultChecked={autoScrollWhenPlay.current}
          onChange={e => (autoScrollWhenPlay.current = e)}
          style={{ marginBottom: 20 }}
        />

        <Button
          size="small"
          onClick={() => {
            setScale(prevScale => Math.min(prevScale + 0.1, 2)); // Увеличение масштаба
          }}
        >
          Zoom -
        </Button>
        <Button
          size="small"
          onClick={() => {
            setScale(prevScale => Math.max(prevScale - 0.1, 0.5));
          }}
        >
          Zoom +
        </Button>
        <TimelinePlayer
          scale={scale}
          scaleWidth={scaleWidth}
          startLeft={startLeft}
          timelineState={timelineState}
          autoScrollWhenPlay={autoScrollWhenPlay}
        />
      </Space>
      <Divider />

      <Timeline
        style={{ width: '100%', height: '200px' }}
        scale={scale}
        scaleWidth={scaleWidth}
        startLeft={startLeft}
        autoScroll={true}
        ref={timelineState}
        editorData={data}
        effects={ProdEffects}
        onChange={(data: StoryTimelineRow[]) => {
          setData(data);
        }}
        getActionRender={(action: StoryTimelineAction, row: StoryTimelineRow) => {
          if (action.effectId === 'StoryFrame') {
            return <EffectStoryFrame action={action} row={row} />;
          }
          if (action.effectId === 'AudioFile') {
            return <EffectAudioFile action={action} row={row} />;
          }

          console.warn('No custom render found for action:', action);
          return null;
        }}
      />
      <Divider />
      <video ref={videoRef} style={{ border: '1px solid #ccc', width: '710px', height: '400px', marginBottom: 16 }} controls />
    </>
  );
};

export default TimelineEditor;
